import { useTranslation } from 'react-i18next';
import { Checkbox, Divider, Radio, RadioGroup, Stack, styled, Text, Tooltip } from 'shell/components';
import { QuizQuestionMode, QuizRubricType } from 'utils/quizRubric';

const QuizRubricOptions = ({ device, form, setForm, handleRubricTypeChange, handleMapCriteriaByOrderChange }) => {
    const { t } = useTranslation('quiz');
    const isHolistic = form.rubricType === QuizRubricType.HOLISTIC;
    const types = [
        {
            value: QuizRubricType.HOLISTIC,
            title: t('rubric.holistic', 'Holistic Rubric'),
            description: t('rubric.holistic-description', 'One rubric applies across all questions in this quiz'),
        },
        {
            value: QuizRubricType.QUESTION,
            title: t('rubric.question', 'Question By Question Rubric'),
            description: t('rubric.question-description', 'Each question uses its own criterion from the rubric.'),
        },
    ];
    const modes = [
        {
            value: QuizQuestionMode.INTERDEPENDENT,
            title: t('rubric.interdependent', 'Interdependent Questions'),
            description: t(
                'rubric.interdependent-description',
                'Later questions build on earlier answers, so TimelyGrader reads the full attempt before grading any one question'
            ),
        },
        {
            value: QuizQuestionMode.STANDALONE,
            title: t('rubric.standalone', 'Standalone Questions'),
            description: t(
                'rubric.standalone-description',
                "Each question is graded independently, using only that question's own response."
            ),
        },
    ];

    return (
        <RubricOptionsCard spacing={5}>
            <Stack spacing={2}>
                <Stack direction={'row'} alignItems={'center'} spacing={1}>
                    <Text id={'quiz-rubric-type-label'} weight={'medium'}>
                        {t('rubric.type', 'Rubric Type')}
                    </Text>
                    <Tooltip
                        type={'info'}
                        content={t(
                            'rubric.type-tooltip',
                            'Choose whether the whole rubric applies to every question or individual criteria are assigned to each question.'
                        )}
                    />
                </Stack>
                <RadioGroup
                    sx={{ flexDirection: device === 'mobile' ? 'column' : 'row', gap: 3 }}
                    aria-labelledby={'quiz-rubric-type-label'}
                    name={'quiz-rubric-type'}
                    value={form.rubricType}
                    onChange={(event) => handleRubricTypeChange(event.target.value)}
                >
                    {types.map(({ value, title, description }) => (
                        <Radio
                            sx={(theme) => ({
                                flex: 1,
                                minWidth: 0,
                                p: 5,
                                m: 0,
                                cursor: 'pointer',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderRadius: '16px',
                                border: `1px solid ${
                                    form.rubricType === value
                                        ? theme.palette.ensightful.primary[500]
                                        : theme.palette.ensightful.neutral[300]
                                }`,
                                background:
                                    form.rubricType === value
                                        ? theme.palette.ensightful.primary[50]
                                        : theme.palette.ensightful.shades[0],
                                '&:hover': {
                                    borderColor: theme.palette.ensightful.primary[500],
                                },
                            })}
                            key={value}
                            value={value}
                            labelPlacement={'start'}
                            label={
                                <Stack spacing={3} sx={{ flex: 1, minWidth: 0 }}>
                                    <Text weight={'medium'}>{title}</Text>
                                    <Text color={'light'}>{description}</Text>
                                </Stack>
                            }
                        />
                    ))}
                </RadioGroup>
                {isHolistic && (
                    <Text color={'primary'}>
                        {t(
                            'rubric.holistic-note',
                            "Reads the full attempt when grading each question. A holistic rubric can't grade questions in isolation."
                        )}
                    </Text>
                )}
            </Stack>
            {!isHolistic && (
                <Stack spacing={3} sx={{ pl: device === 'mobile' ? 0 : 4 }}>
                    <Text id={'quiz-question-mode-label'} weight={'medium'}>
                        {t('rubric.quiz-type', 'Quiz Type: How are the questions being graded?')}
                    </Text>
                    <RadioGroup
                        aria-labelledby={'quiz-question-mode-label'}
                        name={'quiz-question-mode'}
                        value={form.questionMode}
                        onChange={(event) => setForm((current) => ({ ...current, questionMode: event.target.value }))}
                    >
                        <Stack spacing={3}>
                            {modes.map(({ value, title, description }) => (
                                <Radio
                                    sx={{ width: '100%', cursor: 'pointer', alignItems: 'flex-start' }}
                                    key={value}
                                    value={value}
                                    label={
                                        <Stack spacing={1} sx={{ minWidth: 0 }}>
                                            <Text weight={'medium'}>{title}</Text>
                                            <Text color={'light'}>{description}</Text>
                                        </Stack>
                                    }
                                />
                            ))}
                        </Stack>
                    </RadioGroup>
                </Stack>
            )}
            {!isHolistic && (
                <>
                    <Divider />
                    <Stack sx={{ mt: -2 }}>
                        <Checkbox
                            label={t('rubric.map-by-order', 'Map criteria by order automatically')}
                            noWrap={false}
                            checked={form.mapCriteriaByOrder}
                            onChange={(_, checked) => handleMapCriteriaByOrderChange(checked)}
                        />
                        <Text color={'light'} sx={{ pl: 11 }}>
                            {t(
                                'rubric.map-description',
                                'Turning this on matches each question to a criterion based on their order in the quiz and rubric. Leave it off to assign criteria yourself'
                            )}
                        </Text>
                    </Stack>
                </>
            )}
        </RubricOptionsCard>
    );
};

const RubricOptionsCard = styled(Stack)(({ theme }) => ({
    padding: theme.spacing(4),
    border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
    borderRadius: '16px',
}));

export default QuizRubricOptions;
